import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { useWatch } from 'react-hook-form';

import type { FormFields } from './types';

import type { CsvExportDateTimeValue } from 'lib/date/csvExport';
import FormFieldText from 'ui/shared/forms/fields/FormFieldText';
import IconSvg from 'ui/shared/IconSvg';
import type { IconName } from 'ui/shared/IconSvg';

interface Props {
  name: 'from' | 'to';
  maxDateTime: CsvExportDateTimeValue;
}

type InputRef = React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;

const iconStyles = {
  boxSize: '16px',
  flexShrink: 0,
  pointerEvents: 'none',
} as const;

interface PickerTriggerButtonProps {
  iconName: IconName;
  iconColor: string;
  testId: string;
  onMouseDown: React.MouseEventHandler<HTMLButtonElement>;
}

const PickerTriggerButton = ({ iconName, iconColor, testId, onMouseDown }: PickerTriggerButtonProps) => {
  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      onMouseDown={ onMouseDown }
    >
      <IconSvg data-testid={ testId } name={ iconName } color={ iconColor } { ...iconStyles }/>
    </Box>
  );
};

const triggerInputPicker = (event: React.MouseEvent, input: HTMLInputElement | HTMLTextAreaElement | null) => {
  event.preventDefault();
  if (!input) {
    return;
  }

  const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
  pickerInput.showPicker?.();
  pickerInput.focus();
};

const CsvExportFormField = ({ name, maxDateTime }: Props) => {
  const dateFieldName = `${ name }.date` as const;
  const timeFieldName = `${ name }.time` as const;
  const label = name === 'from' ? 'From' : 'To';
  const dateInputRef = React.useRef<InputRef | null>(null);
  const timeInputRef = React.useRef<InputRef | null>(null);
  const selectedDate = useWatch<FormFields, typeof dateFieldName>({ name: dateFieldName });
  const maxTime = name === 'to' && selectedDate === maxDateTime.date ? maxDateTime.time : undefined;

  const labelColor = useColorModeValue('#616161', '#A5A5A5');
  const inputBg = useColorModeValue('#FFFFFF', 'black');
  const inputColor = useColorModeValue('#000000', 'white');
  const inputBorderColor = useColorModeValue('#E9ECEF', 'gray.300');
  const iconColor = useColorModeValue('#616161', '#BABABA');
  const arrowIconColor = useColorModeValue('#616161', 'white');

  const fieldLabelStyles = {
    color: labelColor,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '19.04px',
  } as const;

  const inputSx = {
    bgColor: `${ inputBg } !important`,
    height: '48px !important',
    minHeight: '48px !important',
    padding: '12px 16px !important',
    border: '1px solid !important',
    borderColor: `${ inputBorderColor } !important`,
    borderRadius: '8px !important',
    boxShadow: 'none !important',
    color: `${ inputColor } !important`,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '24px',
    '&:focus, &:focus-visible': {
      borderColor: '#14DCAC !important',
      boxShadow: 'none !important',
    },
    '&::-webkit-calendar-picker-indicator': {
      opacity: 0,
      cursor: 'pointer',
    },
  } as const;

  const handleDatePickerMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    triggerInputPicker(event, dateInputRef.current?.current ?? null);
  }, []);

  const handleTimePickerMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    triggerInputPicker(event, timeInputRef.current?.current ?? null);
  }, []);

  const renderDateRightElement = React.useCallback(({ inputRef }: { inputRef?: InputRef }) => {
    dateInputRef.current = inputRef ?? null;

    return (
      <PickerTriggerButton
        testId="csv-export-date-icon"
        iconName="csv-export/calendar-due"
        iconColor={ iconColor }
        onMouseDown={ handleDatePickerMouseDown }
      />
    );
  }, [ handleDatePickerMouseDown, iconColor ]);

  const renderTimeRightElement = React.useCallback(({ inputRef }: { inputRef?: InputRef }) => {
    timeInputRef.current = inputRef ?? null;

    return (
      <PickerTriggerButton
        testId="csv-export-time-icon"
        iconName="csv-export/arrow-up"
        iconColor={ arrowIconColor }
        onMouseDown={ handleTimePickerMouseDown }
      />
    );
  }, [ handleTimePickerMouseDown, arrowIconColor ]);

  return (
    <Flex
      alignItems="flex-start"
      gap={ 4 }
      direction={{ base: 'column', md: 'row' }}
      sx={{
        '.chakra-form__required-indicator': {
          display: 'none',
        },
      }}
    >
      <Box flex="1" minW={{ base: '100%', md: '0' }}>
        <Text mb={ 1 } { ...fieldLabelStyles }>
          { label }
        </Text>
        <FormFieldText<FormFields, typeof dateFieldName>
          name={ dateFieldName }
          type="date"
          max={ maxDateTime.date }
          ariaLabel={ `${ label } date` }
          placeholder=""
          isRequired
          size="sm_md"
          inputSx={ inputSx }
          rightElement={ renderDateRightElement }
          maxW="none"
        />
      </Box>
      <Box flex="1" minW={{ base: '100%', md: '0' }}>
        <Text mb={ 1 } visibility="hidden" { ...fieldLabelStyles }>
          { label }
        </Text>
        <FormFieldText<FormFields, typeof timeFieldName>
          name={ timeFieldName }
          type="time"
          step="1"
          max={ maxTime }
          ariaLabel={ `${ label } time` }
          placeholder=""
          isRequired
          size="sm_md"
          inputSx={ inputSx }
          rightElement={ renderTimeRightElement }
          maxW="none"
        />
      </Box>
    </Flex>
  );
};

export default React.memo(CsvExportFormField);
